import type React from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import type { KintoneAvailableApp, SelectedApp } from "~/interface/reverse";
import { icons as kintonePresetIcons } from "../../../config/kintoneIcons";
import searchIcon from "../../../images/search.svg";
import InfoTooltip from "../../ui/infoTooltip";
import styles from "./index.module.scss";

interface AppSelectionStepProps {
  selectedApps: string[];
  selectedAppDetails?: SelectedApp[];
  onSelectionChange: (selectedIds: string[], selectedAppDetails: SelectedApp[]) => void;
  availableApps?: KintoneAvailableApp[];
}

const AppSelectionStep: React.FC<AppSelectionStepProps> = ({
  selectedApps,
  onSelectionChange,
  selectedAppDetails,
  availableApps,
}) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [iconUrls, setIconUrls] = useState<Record<string, string>>({});

  // Use availableApps from API - wrapped in useMemo to stabilize the reference
  const apps = useMemo(() => availableApps ?? [], [availableApps]);
  const totalApps = apps.length;

  const filteredApps = apps.filter((app) =>
    app.name.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  const buildSelectedDetails = useCallback(
    (ids: string[]): SelectedApp[] => {
      return ids.map((id) => {
        const app = apps.find((a) => a.appId === id);
        const existing = selectedAppDetails?.find((detail) => detail.id === id);

        const detail: SelectedApp = {
          id,
          name: app?.name || existing?.name || "",
          description: app?.description || existing?.description || "",
          iconFileKey: app?.iconFileKey ?? existing?.iconFileKey ?? null,
        };

        const resolvedIconUrl = iconUrls[id] ?? existing?.iconUrl;
        if (resolvedIconUrl) {
          detail.iconUrl = resolvedIconUrl;
        }

        return detail;
      });
    },
    [apps, iconUrls, selectedAppDetails],
  );

  const handleAppClick = (appId: string) => {
    let newSelectedIds: string[];
    let newSelectedDetails: SelectedApp[];

    if (selectedApps.includes(appId)) {
      newSelectedIds = selectedApps.filter((id) => id !== appId);
    } else {
      newSelectedIds = [...selectedApps, appId];
    }

    // Get the app details for the selected IDs
    newSelectedDetails = buildSelectedDetails(newSelectedIds);

    onSelectionChange(newSelectedIds, newSelectedDetails);
  };

  useEffect(() => {
    const iconMap: Record<string, string> = {};

    apps.forEach((app) => {
      const appId = app.appId;
      const iconKeyRaw = app.iconFileKey?.trim();

      if (!appId || !iconKeyRaw) {
        return;
      }

      if (/^https?:\/\//i.test(iconKeyRaw)) {
        iconMap[appId] = iconKeyRaw;
        return;
      }

      const normalizedKey = iconKeyRaw.toUpperCase();
      const presetUrl = (kintonePresetIcons as Record<string, string>)[normalizedKey];
      if (normalizedKey.startsWith("APP") && presetUrl) {
        iconMap[appId] = presetUrl;
      }
    });

    setIconUrls(iconMap);
  }, [apps]);

  useEffect(() => {
    if (!selectedApps.length) {
      return;
    }

    const updatedDetails = buildSelectedDetails(selectedApps);

    const hasDifference =
      updatedDetails.length !== (selectedAppDetails?.length || 0) ||
      updatedDetails.some((detail) => {
        const existing = selectedAppDetails?.find((item) => item.id === detail.id);
        if (!existing) {
          return true;
        }

        return (
          existing.name !== detail.name ||
          existing.description !== detail.description ||
          existing.iconUrl !== detail.iconUrl ||
          existing.iconFileKey !== detail.iconFileKey
        );
      });

    if (hasDifference) {
      onSelectionChange(selectedApps.slice(), updatedDetails);
    }
  }, [selectedApps, selectedAppDetails, onSelectionChange, buildSelectedDetails]);

  return (
    <div className={styles["appSelectionContainer"]}>
      <div className={styles["appsMeta"]}>
        <span className={styles["appsCount"]}>取得アプリ数: {totalApps}</span>
        <InfoTooltip
          message="権限設定により取得できないアプリが存在する場合があります。必要な権限を持つアカウントでの取得をお試しください。"
          position="bottom"
        />
      </div>
      <div className={styles["searchInputWrapper"]}>
        <input
          type="text"
          placeholder="アプリ名で検索"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className={styles["searchInput"]}
        />
        <img src={searchIcon} alt="検索" className={styles["searchIcon"]} />
      </div>

      <div className={styles["appsGrid"]}>
        {filteredApps.map((app) => {
          const appId = app.appId || "";
          return (
            <div
              key={appId}
              className={`${styles["appCard"]} ${
                selectedApps.includes(appId) ? styles["selected"] : ""
              }`}
              onClick={() => handleAppClick(appId)}
            >
              <div className={styles["appHeader"]}>
                <div
                  className={styles["appIcon"]}
                  style={
                    iconUrls[appId] ? { backgroundImage: `url(${iconUrls[appId]})` } : undefined
                  }
                />
                <div className={styles["appInfo"]}>
                  <h3 className={styles["appName"]} title={app.name}>
                    {app.name}
                  </h3>
                  <span className={styles["appId"]}>ID: {app.appId}</span>
                </div>
              </div>
              <p
                className={styles["appDescription"]}
                dangerouslySetInnerHTML={{ __html: app.description }}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default AppSelectionStep;
